import { create } from "zustand";
import api from "../lib/api";
import { settings } from "../lib/settings";
import { getCookie } from "../lib/cookies";
import { Thread, Message, Assistant, Organization } from "../types";
import Cookies from "js-cookie";

interface ChatState {
  threads: Thread[];
  currentThread: Thread | null;
  messages: Message[];
  assistants: Assistant[];
  selectedAssistant: Assistant | null;
  isWaitingResponse: boolean;
  fetchThreads: () => Promise<void>;
  fetchMessages: (threadId: string) => Promise<void>;
  sendMessage: (
    assistantUid: string,
    threadId: string,
    content: string
  ) => Promise<void>;
  fetchAssistants: () => Promise<void>;
  createThread: (assistantId: string) => Promise<string>;
  deleteThread: (threadId: string) => Promise<void>;
  renameThread: (threadId: string, name: string) => Promise<void>;
  setCurrentThread: (thread: Thread | null) => void;
  setSelectedAssistant: (assistant: Assistant | null) => void;
  org: Organization | null;
  org_id: string | null;
}

export const useChatStore = create<ChatState>((set, get) => ({
  threads: [],
  currentThread: null,
  messages: [],
  assistants: [],
  selectedAssistant: null,
  isWaitingResponse: false,
  org: Cookies.get(settings.cookies.organization)
    ? JSON.parse(Cookies.get(settings.cookies.organization)!)
    : null,
  org_id: Cookies.get(settings.cookies.organization)
    ? JSON.parse(Cookies.get(settings.cookies.organization)!).uid.toString()
    : null,

  fetchThreads: async () => {
    try {
      const response = await api.get(settings.api.chat.get_threads_by_user);
      if (response.data && response.data.threads) {
        set({ threads: response.data.threads });
      }
    } catch (error) {
      console.error("Error fetching threads:", error);
      throw error;
    }
  },

  fetchAssistants: async () => {
    try {
      const storedUser = getCookie(settings.cookies.user);
      if (!storedUser) {
        throw new Error("No user data found in cookies");
      }

      const userData = JSON.parse(storedUser);
      if (!userData.uid) {
        throw new Error("No uid found in user data");
      }

      const response = await api.get(
        settings.api.chat.get_groups(userData.uid)
      );
      if (
        response.data &&
        response.data.user_access &&
        response.data.user_access[0]?.bots
      ) {
        set({ assistants: response.data.user_access[0].bots });
      }
    } catch (error) {
      console.error("Error fetching assistants:", error);
      throw error;
    }
  },

  createThread: async (assistantId: string) => {
    try {
      const storedUser = getCookie(settings.cookies.user);
      const storedOrg = getCookie(settings.cookies.organization);
      if (!storedUser || !storedOrg) {
        throw new Error("No user or organization data found in cookies");
      }

      const userData = JSON.parse(storedUser);
      const orgData = JSON.parse(storedOrg);
      if (!userData.uid || !orgData.uid) {
        throw new Error("Missing user data (uid) or organization data (uid)");
      }

      const state = get();
      const selectedBot = state.assistants.find(
        (bot) => bot.id === assistantId
      );
      if (!selectedBot) {
        throw new Error("Selected bot not found");
      }

      const response = await api.post(settings.api.chat.create_thread, {
        bot_id: selectedBot.id,
        user_id: userData.id,
        org_id: orgData.id,
      });

      const newThread = response.data;

      // Fetch updated threads list
      await get().fetchThreads();

      // Get current state after fetching
      const currentState = get();

      // Find the newly created thread in the updated list
      const updatedThread =
        currentState.threads.find(
          (t: Thread) => t.id === newThread.thread_id
        ) || newThread;

      set((state) => ({
        threads: [...state.threads],
        currentThread: updatedThread,
      }));

      return newThread.thread_id;
    } catch (error) {
      console.error("Error creating thread:", error);
      throw error;
    }
  },

  setCurrentThread: (thread: Thread | null) => {
    set({ currentThread: thread });
  },

  setSelectedAssistant: (assistant: Assistant | null) => {
    set({ selectedAssistant: assistant });
  },

  fetchMessages: async (threadId: string) => {
    try {
      const response = await api.get(settings.api.chat.messages(threadId));
      if (response.data && response.data.messages) {
        // Sort messages by creation time
        const sortedMessages = [...response.data.messages].sort(
          (a: Message, b: Message) =>
            Number(a.created_at) - Number(b.created_at)
        );
        set({ messages: sortedMessages });
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw error;
    }
  },

  sendMessage: async (
    assistantUid: string,
    threadId: string,
    content: string
  ) => {
    try {
      // Get current thread to get assistant_id
      const state = get();
      const currentThread = state.currentThread;
      if (!currentThread?.bot_id) {
        throw new Error("No bot_id found in current thread");
      }

      // Find selected assistant to get uid
      const selectedBot = state.assistants.find(
        (bot) => bot.id === assistantUid
      );
      if (!selectedBot) {
        throw new Error("Selected bot not found");
      }

      // Create and add user message immediately
      const userMessage: Message = {
        message_id: `temp_${Date.now()}`,
        role: "user",
        content: content,
        created_at: Date.now(),
        thread_id: threadId,
        assistant_id: selectedBot.uid.toString(),
      };

      set((state) => ({
        messages: [...state.messages, userMessage],
        isWaitingResponse: true,
      }));

      // Send message to server with string UIDs
      const response = await api.post(settings.api.chat.send, {
        thread_id: currentThread.id,
        text: content,
      });

      // Update user message with actual ID
      const updatedUserMessage: Message = {
        ...userMessage,
        message_id: response.data.user_message_id,
      };

      // Create assistant message if there's a response
      const assistantMessage: Message | null = response.data.assistant_response
        ? {
            message_id: response.data.message_id || `assistant_${Date.now()}`,
            role: "assistant",
            content: response.data.assistant_response,
            created_at: Date.now(),
            thread_id: threadId,
            assistant_id: selectedBot.uid.toString(),
          }
        : null;

      // Update messages in state
      set((state) => ({
        messages: [
          ...state.messages.filter(
            (m) => m.message_id !== userMessage.message_id
          ),
          updatedUserMessage,
          ...(assistantMessage ? [assistantMessage] : []),
        ],
        isWaitingResponse: false,
      }));
    } catch (error) {
      set({ isWaitingResponse: false });
      console.error("Failed to send message:", error);
      throw error;
    }
  },

  deleteThread: async (threadId: string) => {
    try {
      // Find thread to get its uid
      const state = get();
      const thread = state.threads.find((t) => t.id === threadId);
      if (!thread?.id) {
        throw new Error("Thread not found or no id available");
      }

      await api.delete(settings.api.chat.delete_thread(thread.id));

      // Update state
      const isCurrentThread = state.currentThread?.id === threadId;

      set((state) => ({
        threads: state.threads.filter((t) => t.id !== threadId),
        currentThread: isCurrentThread ? null : state.currentThread,
        messages: isCurrentThread ? [] : state.messages,
      }));

      return;
    } catch (error) {
      console.error("Error deleting thread:", error);
      throw error;
    }
  },

  renameThread: async (threadId: string, name: string) => {
    try {
      const state = get();
      const thread = state.threads.find((t) => t.id === threadId);
      if (!thread?.id) {
        throw new Error("Thread not found or no uid available");
      }

      // Use thread.uid (numeric ID) for the API path
      await api.post(`${settings.api.chat.rename_thread}/${thread.id}`, {
        name,
      });

      // Update state
      set((state) => ({
        threads: state.threads.map((t) =>
          t.id === threadId ? { ...t, name } : t
        ),
        currentThread:
          state.currentThread?.id === threadId
            ? { ...state.currentThread, name }
            : state.currentThread,
      }));

      return;
    } catch (error) {
      console.error("Error renaming thread:", error);
      throw error;
    }
  },
}));
